import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import LoginBackground from "../Shared/loginBackground";
import Logo from "../../assets/image/logo.png";
import loginService from "../../services/loginService";
import { VENDOR_LOGIN_PATH } from "../../config/vendorAuthPaths";
import {
  recoverPasswordEmailSchema,
  vendorNewPasswordSchema,
} from "../../services/schemas/loginSchemas";

const PAPER_SX = {
  p: { xs: 6, sm: 6, md: 6 },
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: { xs: "100%", sm: "450px", md: "450px" },
  minHeight: { xs: "auto", sm: "610px" },
  borderRadius: 2,
  backgroundColor: "#ffffff",
  boxShadow: "2px 2px 18px 5px rgba(0, 0, 0, 0.6)",
  justifyContent: "center",
};

const INNER_BOX_SX = {
  width: "86%",
  height: "90%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: 5,
};

const FILLED_INPUT_SX = {
  backgroundColor: "#edf3ff",
  borderRadius: "8px",
  fontStyle: "italic",
};

function filledTextFieldProps() {
  return {
    disableUnderline: true,
    sx: FILLED_INPUT_SX,
  };
}

function labelSx(text) {
  return <span style={{ color: "#0b3d87", fontStyle: "italic", fontFamily: "Rubik" }}>{text}</span>;
}

/**
 * Stesso flusso e stesso token per entrambe le modalita': cambiano solo i testi.
 * "setup" e' per gli account vendor creati prima della produzione, che una password non
 * l'hanno mai avuta: parlare di "recupero" o "nuova password" li confonderebbe.
 */
const TESTI = {
  reset: {
    titoloPagina: "Wow - Recupero password Vendor",
    intestazione: "Recupero password",
    introEmail: "Inserisci la tua email: ti invieremo il link per reimpostare la password.",
    bottoneEmail: "INVIA LINK DI RECUPERO",
    introPassword: "Inserisci una nuova password per il tuo account",
    etichettaPassword: "Nuova password",
    bottonePassword: "IMPOSTA PASSWORD",
  },
  setup: {
    titoloPagina: "Wow - Imposta la password Vendor",
    intestazione: "Imposta una password",
    introEmail: "Inserisci l'email del tuo account vendor: ti invieremo il link per impostare la password.",
    bottoneEmail: "INVIA LINK",
    introPassword: "Scegli la password con cui accederai al portale vendor",
    etichettaPassword: "Password",
    bottonePassword: "IMPOSTA PASSWORD",
  },
};

function readApiError(error) {
  const raw = error?.response?.data;
  if (raw && typeof raw.message === "string" && raw.message.length > 0) {
    return raw.message;
  }
  return "Operazione non riuscita. Riprova.";
}

function EmailRequestSection({ testi }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const emailParsed = recoverPasswordEmailSchema.safeParse(email);
    if (!emailParsed.success) {
      setError(emailParsed.error.issues[0]?.message ?? "Email non valida.");
      return;
    }
    setLoading(true);
    try {
      const res = await loginService.requestRecoverPassword({ email: emailParsed.data });
      const msg = res?.data?.message;
      setSuccess(typeof msg === "string" && msg.length > 0 ? msg : "Richiesta inviata. Controlla la posta.");
    } catch (err) {
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      {error ? (
        <Alert severity="error" sx={{ width: "100%" }} aria-live="assertive">
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ width: "100%" }} aria-live="polite">
          {success}
        </Alert>
      ) : null}
      <Typography variant="body2" sx={{ color: "#0b3d87", fontFamily: "Rubik", textAlign: "center" }}>
        {testi.introEmail}
      </Typography>
      <TextField
        fullWidth
        id="recover-email"
        label={labelSx("Email")}
        type="email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        required
        autoComplete="email"
        variant="filled"
        InputProps={filledTextFieldProps()}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{
          py: 1.4,
          fontFamily: "Rubik",
          fontSize: "1rem",
          fontWeight: "bold",
          borderRadius: "999px",
          backgroundColor: "#0b3d87",
          "&:hover": { backgroundColor: "#062a63" },
          minHeight: 48,
        }}
      >
        {loading ? "Invio in corso..." : testi.bottoneEmail}
      </Button>
    </Box>
  );
}

EmailRequestSection.propTypes = {
  testi: PropTypes.object.isRequired,
};

function NewPasswordSection({ token, testi }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const toggleShowPassword = useCallback(() => {
    setShowPassword((v) => !v);
  }, []);

  const toggleShowConfirm = useCallback(() => {
    setShowConfirm((v) => !v);
  }, []);

  const handleMouseDownPassword = useCallback((ev) => {
    ev.preventDefault();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const pwdParsed = vendorNewPasswordSchema.safeParse(password);
    if (!pwdParsed.success) {
      setError(pwdParsed.error.issues[0]?.message ?? "Password non valida.");
      return;
    }
    if (password !== confirm) {
      setError("Le password non coincidono.");
      return;
    }
    setLoading(true);
    try {
      await loginService.recoverPassword({
        token,
        password: globalThis.btoa(password),
      });
      navigate(VENDOR_LOGIN_PATH, { replace: true, state: { passwordResetOk: true } });
    } catch (err) {
      setError(readApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      {error ? (
        <Alert severity="error" sx={{ width: "100%" }} aria-live="assertive">
          {error}
        </Alert>
      ) : null}
      <Typography variant="body2" sx={{ color: "#0b3d87", fontFamily: "Rubik", textAlign: "center"}}>
        {testi.introPassword}
      </Typography>
      <TextField
        fullWidth
        id="new-password"
        label={labelSx(testi.etichettaPassword)}
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(ev) => setPassword(ev.target.value)}
        required
        autoComplete="new-password"
        variant="filled"
        inputProps={{ "data-testid": "vendor-reset-new-password" }}
        InputProps={{
          ...filledTextFieldProps(),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                onClick={toggleShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                tabIndex={-1}
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        fullWidth
        id="confirm-password"
        label={labelSx("Conferma password")}
        type={showConfirm ? "text" : "password"}
        value={confirm}
        onChange={(ev) => setConfirm(ev.target.value)}
        required
        autoComplete="new-password"
        variant="filled"
        inputProps={{ "data-testid": "vendor-reset-confirm-password" }}
        InputProps={{
          ...filledTextFieldProps(),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showConfirm ? "Nascondi conferma password" : "Mostra conferma password"}
                onClick={toggleShowConfirm}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                tabIndex={-1}
                size="small"
              >
                {showConfirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{
          py: 1.4,
          fontFamily: "Rubik",
          fontSize: "1rem",
          fontWeight: "bold",
          borderRadius: "999px",
          backgroundColor: "#0b3d87",
          "&:hover": { backgroundColor: "#062a63" },
          minHeight: 48,
        }}
      >
        {loading ? "Salvataggio..." : testi.bottonePassword}
      </Button>
    </Box>
  );
}

NewPasswordSection.propTypes = {
  token: PropTypes.string.isRequired,
  testi: PropTypes.object.isRequired,
};

export default function PasswordResetPage({ mode }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const testi = TESTI[mode] ?? TESTI.reset;

  const headerBlock = (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, mb: 0 }}>
      <Box
        component={RouterLink}
        to={VENDOR_LOGIN_PATH}
        sx={{ textDecoration: "none", color: "inherit" }}
        aria-label="Torna al login vendor"
      >
        <img
          src={Logo}
          alt="Logo Wow che prezzi"
          height="90"
          style={{
            objectFit: "contain",
            width: "auto",
            maxHeight: "110px",
            display: "block",
          }}
        />
      </Box>
      <Box sx={{ textAlign: "center", width: "100%" }}>
        <Typography variant="h5" sx={{ color: "#0b3d87", mb: 1, fontFamily: "Rubik", fontWeight: "bold" }}>
          {testi.intestazione}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <LoginBackground title={testi.titoloPagina}>
      <Paper sx={PAPER_SX}>
        <Box sx={INNER_BOX_SX}>
          {headerBlock}
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
            {token ? (
              <NewPasswordSection token={token} testi={testi} />
            ) : (
              <EmailRequestSection testi={testi} />
            )}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Link component={RouterLink} to={VENDOR_LOGIN_PATH} underline="hover" sx={{ fontFamily: "Rubik", color: "#0b3d87" }}>
                Torna al login
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>
    </LoginBackground>
  );
}

PasswordResetPage.propTypes = {
  mode: PropTypes.oneOf(["reset", "setup"]),
};

PasswordResetPage.defaultProps = {
  mode: "reset",
};
